import React, { useRef, useState } from 'react';
import { Alert, Button, Card, Col, Form, Modal, Row, Table } from '@themesberg/react-bootstrap';
import geoNow from '../utils/geonow';
import ReactSelect from 'react-select';
import LogDatePicker from './LogDatePicker';
import { defaultLogDateRange, formatLogJson, formatLogTime, makeLogFilters, notificationStatuses } from '../utils/valueNotifyLogs';

const channels = { 0: 'LINE', 1: 'GNS SMS', 2: 'Email' };
const kinds = { Trigger: '觸發評估', Delivery: '通道發送／抑制' };

export default function ValueNotifyLogs({ token, projects = [], users = [] }) {
    const [filters, setFilters] = useState(() => ({ projectIds: [], userId: '', kind: '', channel: '', status: '', ...defaultLogDateRange(), triggerId: '' }));
    const [userProjects, setUserProjects] = useState([]);
    const [projectsLoading, setProjectsLoading] = useState(false);
    const [projectsError, setProjectsError] = useState('');
    const projectRequest = useRef(0);
    const [result, setResult] = useState(null);
    const [appliedFilters, setAppliedFilters] = useState(filters);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState('');
    const [showDetail, setShowDetail] = useState(false);
    const detailRequest = useRef(0);
    const listRequest = useRef(0);

    const selectUser = async user => {
        const requestId = ++projectRequest.current;
        ++listRequest.current;
        setLoading(false);
        setResult(null);
        setError('');
        setProjectsError('');
        setUserProjects([]);
        const userId = user?.id || '';
        setFilters(previous => ({ ...previous, userId, projectIds: [], triggerId: '' }));
        setProjectsLoading(!!userId);
        if (!userId) return;
        try {
            const response = await geoNow.api.valueNotifyLogs.projectsForUser(token, userId);
            if (requestId !== projectRequest.current) return;
            if (response.status !== 1) throw new Error(response.message || '載入使用者專案失敗');
            setUserProjects(response.message);
            setFilters(previous => ({ ...previous, projectIds: response.message.map(project => project.projectId) }));
        } catch (e) { if (requestId === projectRequest.current) setProjectsError(e.message); }
        finally { if (requestId === projectRequest.current) setProjectsLoading(false); }
    };
    const projectOptions = (filters.userId ? userProjects : projects).map(project => ({
        value: project.projectId || project.id,
        label: project.projectName || project.name || project.projectId || project.id
    }));

    const query = async (nextFilters = filters, page = 1) => {
        const requestId = ++listRequest.current;
        setLoading(true);
        setError('');
        try {
            const payload = makeLogFilters(nextFilters, page);
            const response = await geoNow.api.valueNotifyLogs.list(token, payload);
            if (requestId !== listRequest.current) return;
            if (response.status !== 1) throw new Error(response.message || '查詢失敗');
            setResult(response.message);
            setAppliedFilters(nextFilters);
        } catch (e) {
            if (requestId === listRequest.current) { setError(e.message); setResult(null); }
        } finally { if (requestId === listRequest.current) setLoading(false); }
    };

    const openDetail = async id => {
        const requestId = ++detailRequest.current;
        setShowDetail(true);
        setDetail(null);
        setDetailError('');
        setDetailLoading(true);
        try {
            const response = await geoNow.api.valueNotifyLogs.detail(token, id);
            if (requestId !== detailRequest.current) return;
            if (response.status !== 1) throw new Error(response.message || '讀取失敗');
            setDetail(response.message);
        } catch (e) { if (requestId === detailRequest.current) setDetailError(e.message); }
        finally { if (requestId === detailRequest.current) setDetailLoading(false); }
    };

    const related = triggerId => {
        ++projectRequest.current;
        setProjectsLoading(false);
        setProjectsError('');
        const next = { ...filters, triggerId, kind: '', channel: '', status: '', from: '', to: '', projectIds: [], userId: '' };
        setFilters(next);
        setShowDetail(false);
        query(next);
    };
    const change = (name, value) => setFilters(previous => ({ ...previous, [name]: value }));
    const jsonBlock = (title, value) => value && <details className="mb-3">
        <summary>{title}</summary>
        <pre className="bg-light p-3 mt-2" style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', maxHeight: '50vh' }}>{formatLogJson(value)}</pre>
    </details>;

    return <Card border="light" className="bg-white shadow-sm mb-4">
        <Card.Body>
            <h5>ValueNotify 觸發與發送紀錄</h5>
            <p className="text-muted">全站紀錄，僅超級管理員可查詢。每次異常評估與各通道結果分開保存；測試發送不在此列。</p>
            <Alert variant="info">API／SMTP「已接受」不等於每位收件人實際送達。請開啟詳細紀錄，比對當次實際收件人與業者回應。
                Pending 可能是仍在處理或程序中斷；Unknown 表示結果無法確認。紀錄僅涵蓋此功能啟用後的事件。</Alert>
            <Form onSubmit={event => { event.preventDefault(); if (!projectsLoading && !projectsError) query(); }}>
                <Row>
                    <Col md={4}><Form.Group className="mb-3"><Form.Label htmlFor="notify-log-user">依使用者載入專案</Form.Label>
                        <ReactSelect inputId="notify-log-user" isClearable options={users.filter(user => !user.isSuperUser)}
                            value={users.find(user => user.id === filters.userId) || null}
                            getOptionValue={user => user.id}
                            getOptionLabel={user => `${user.username} (${[user.firstName, user.lastName].filter(Boolean).join(' ')})`}
                            placeholder="不限使用者（僅列非超級使用者）" onChange={selectUser} />
                    </Form.Group></Col>
                    <Col md={8}><Form.Group className="mb-3"><Form.Label htmlFor="notify-log-projects">專案（可複選）</Form.Label>
                        <ReactSelect inputId="notify-log-projects" isMulti isClearable closeMenuOnSelect={false}
                            isDisabled={projectsLoading || !!projectsError} isLoading={projectsLoading}
                            options={projectOptions} value={projectOptions.filter(option => filters.projectIds.includes(option.value))}
                            onChange={selected => change('projectIds', (selected || []).map(option => option.value))}
                            placeholder={filters.userId ? '該使用者所有可用專案' : '所有專案（含已刪除專案）'} />
                        <small className="text-muted">{filters.userId ? `此使用者有 ${userProjects.length} 個可用專案；清空複選仍只查詢該使用者的專案。` : '未選使用者且未選專案時，查詢全站紀錄。'}</small>
                        {projectsError && <Alert variant="danger" className="mt-2">{projectsError}
                            <Button variant="link" onClick={() => selectUser(users.find(user => user.id === filters.userId) || { id: filters.userId })}>重新載入</Button></Alert>}
                    </Form.Group></Col>
                    <Col md={2}><Form.Group className="mb-3"><Form.Label>種類</Form.Label>
                        <Form.Select value={filters.kind} onChange={e => change('kind', e.target.value)}><option value="">全部</option>
                            {Object.entries(kinds).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                        </Form.Select></Form.Group></Col>
                    <Col md={2}><Form.Group className="mb-3"><Form.Label>通道</Form.Label>
                        <Form.Select value={filters.channel} onChange={e => change('channel', e.target.value)}><option value="">全部</option>
                            {Object.entries(channels).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                        </Form.Select></Form.Group></Col>
                    <Col md={4}><Form.Group className="mb-3"><Form.Label>狀態</Form.Label>
                        <Form.Select value={filters.status} onChange={e => change('status', e.target.value)}><option value="">全部</option>
                            {Object.entries(notificationStatuses).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                        </Form.Select></Form.Group></Col>
                    <Col md={4}><Form.Group className="mb-3"><Form.Label htmlFor="notify-log-from">開始日期（本機時區，00:00 起）</Form.Label>
                        <LogDatePicker id="notify-log-from" value={filters.from} onChange={value => change('from', value)} />
                    </Form.Group></Col>
                    <Col md={4}><Form.Group className="mb-3"><Form.Label htmlFor="notify-log-to">結束日期（含當日全部時間）</Form.Label>
                        <LogDatePicker id="notify-log-to" value={filters.to} onChange={value => change('to', value)} />
                    </Form.Group></Col>
                    <Col md={4} className="mb-3 align-self-end"><Button variant="outline-secondary" onClick={() => setFilters(previous => ({ ...previous, ...defaultLogDateRange() }))}>近三個月（完整月份）</Button></Col>
                </Row>
                {filters.triggerId && <Alert variant="secondary">正在查詢觸發 ID：{filters.triggerId}
                    <Button size="sm" variant="link" onClick={() => { const next = { ...filters, triggerId: '' }; setFilters(next); query(next); }}>解除關聯篩選</Button>
                </Alert>}
                <Button type="submit" disabled={loading || projectsLoading || !!projectsError}>{loading ? '查詢中…' : '查詢／重新整理'}</Button>
            </Form>
            {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
            {result && <>
                <div className="mt-3">共 {result.total} 筆，每頁 {result.pageSize} 筆</div>
                <Table responsive hover className="mt-2">
                    <thead><tr><th>時間（本機）</th><th>專案／種類</th><th>通道／業者</th><th>狀態</th><th>收件數</th><th>摘要</th><th>操作</th></tr></thead>
                    <tbody>{result.items.map(item => <tr key={item.id}>
                        <td>{formatLogTime(item.createdUtcTime)}</td>
                        <td>{item.projectName}<br /><small>{kinds[item.kind] || item.kind}</small></td>
                        <td>{channels[item.channel] || '—'}<br /><small>{item.provider}</small></td>
                        <td>{notificationStatuses[item.status] || item.status}</td>
                        <td>{item.kind === 'Delivery' ? item.recipientCount : '—'}</td>
                        <td style={{ minWidth: 160, maxWidth: 360, whiteSpace: 'normal', overflowWrap: 'anywhere' }}>{item.summary}</td>
                        <td><Button size="sm" variant="outline-primary" onClick={() => openDetail(item.id)}>詳細／Response</Button>
                            <Button size="sm" variant="link" disabled={loading} onClick={() => related(item.triggerId || item.id)}>同次觸發</Button></td>
                    </tr>)}</tbody>
                </Table>
                {result.items.length === 0 && <p>此條件下沒有紀錄。</p>}
                <Button size="sm" disabled={loading || result.page <= 1} onClick={() => query(appliedFilters, result.page - 1)}>上一頁</Button>
                <span className="mx-3">第 {result.page} / {Math.max(1, Math.ceil(result.total / result.pageSize))} 頁</span>
                <Button size="sm" disabled={loading || result.page * result.pageSize >= result.total} onClick={() => query(appliedFilters, result.page + 1)}>下一頁</Button>
            </>}
            <Modal show={showDetail} size="xl" onHide={() => { ++detailRequest.current; setShowDetail(false); }}>
                <Modal.Header closeButton><Modal.Title>ValueNotify 完整紀錄</Modal.Title></Modal.Header>
                <Modal.Body>
                    {detailLoading && <p>讀取中…</p>}
                    {detailError && <Alert variant="danger">{detailError}</Alert>}
                    {detail && <>
                        <p>{detail.projectName} — {kinds[detail.kind]} — {notificationStatuses[detail.status] || detail.status}</p>
                        <p>{detail.summary}</p>
                        <p>建立：{formatLogTime(detail.createdUtcTime)}；完成：{formatLogTime(detail.completedUtcTime)}</p>
                        <p>業者：{detail.provider || '—'}；發送人／帳號來源：{detail.sender || '—'}</p>
                        <p>無訊號測點：{detail.warningCount}；超限測點：{detail.dangerCount}</p>
                        <p>紀錄 ID：{detail.id}<br />額度計次 ID：{detail.deliveryAttemptId || '—'}</p>
                        {detail.kind === 'Delivery' && <div className="mb-3"><strong>當次收件人（{detail.recipientCount}）</strong>
                            <pre className="bg-light p-3" style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{formatLogJson(detail.recipientsJson) || '未設定／尚未解析'}</pre></div>}
                        {jsonBlock('觸發測點／設定快照／本次可發送測點', detail.snapshotJson)}
                        {jsonBlock('生效的頻率與額度設定（含繼承來源）', detail.rateLimitsJson)}
                        {jsonBlock('實際請求內容（不含認證密鑰）', detail.requestJson)}
                        {jsonBlock('完整 Response／SMTP 結果（敏感認證資訊遮蔽）', detail.responseJson)}
                        {jsonBlock('例外詳細資訊', detail.exceptionDetail)}
                        <p className="text-muted">SMS 若有多位收件人，請在 GNS ResponseBody／RemoteMessage 中逐筆檢查業者回傳的目的號碼與狀態。
                            SMTP 不提供成功回應原文，此處保存寄送結果與例外；沒有送達回報時不能判定最終送達。</p>
                        <Button onClick={() => related(detail.triggerId || detail.id)}>查詢同次觸發及各通道紀錄</Button>
                    </>}
                </Modal.Body>
            </Modal>
        </Card.Body>
    </Card>;
}
